import type {FieldTypeId} from 'src/typings';
import type {FieldTypeDefinition} from 'src/features/record/field-types/types';
import type {RecordRow} from 'src/features/record-grid/lib/types';
import type {
    ResolvedRecordFieldsDetail,
    RecordFieldsDetailGridOptions,
    RecordDetailField,
    CustomDisplayField
} from './types';
import {exists} from 'shared/utils/custom-utils';
import {getFieldTypeDefinitionRecursively} from 'src/features/record/field-types/utils';
import {
    extractAllSimpleGridFields,
    getFieldByFieldTypeIdRecursive
} from 'src/features/record-grid/lib/grid-fields';
import {
    isCustomFieldTypeIdField,
    isCustomComponentField,
    isCustomValueField, isValidCustomDisplayField
} from './utils';

const droppedFieldIds = ['fond'];

export function resolveRecordDetailFields(
    fieldTypeDefinitions: FieldTypeDefinition[],
    record: RecordRow,
    options: RecordFieldsDetailGridOptions
): ResolvedRecordFieldsDetail {

    const detailFields = exists(options.customDisplayFields)
        ? createCustomDetailFields(options.customDisplayFields, fieldTypeDefinitions, record)
        : createDefaultDetailFields(fieldTypeDefinitions, record);

    return {detailFields};
}

function createDefaultDetailFields(fieldTypeDefinitions: FieldTypeDefinition[], record: RecordRow): RecordDetailField[] {
    const lowestFields = collectLowestFieldDefinitions(fieldTypeDefinitions);

    return lowestFields.flatMap((definition) => {
        const fields = extractAllSimpleGridFields(getFieldByFieldTypeIdRecursive(record, definition.id));

        return fields.map((field) => ({
            field,
            definition,
            text: getFormattedFieldTypeNameFromDefinitions(fieldTypeDefinitions, definition.id)
        }));
    });
}

function createCustomDetailFields(
    customDisplayFields: CustomDisplayField[],
    fieldTypeDefinitions: FieldTypeDefinition[],
    record: RecordRow
): RecordDetailField[] {
    const result: RecordDetailField[] = [];

    customDisplayFields.forEach((customDisplayField) => {
        if (!isValidCustomDisplayField(customDisplayField)) {
            return;
        }

        if (isCustomFieldTypeIdField(customDisplayField)) {
            const fieldTypeDefinition = getFieldTypeDefinitionRecursively({fieldTypes: fieldTypeDefinitions}, customDisplayField);
            if (!exists(fieldTypeDefinition)) {
                return;
            }

            const fields = extractAllSimpleGridFields(getFieldByFieldTypeIdRecursive(record, customDisplayField));
            if (fields.length === 0) {
                result.push({
                    field: null,
                    definition: fieldTypeDefinition,
                    text: getFormattedFieldTypeNameFromDefinitions(fieldTypeDefinitions, customDisplayField)
                });
                return;
            }

            const detailFields = fields.map((field) => ({
                field,
                definition: fieldTypeDefinition,
                text: getFormattedFieldTypeNameFromDefinitions(fieldTypeDefinitions, customDisplayField)
            }));

            result.push(...detailFields);
        }

        if (isCustomComponentField(customDisplayField)) {
            result.push({
                text: customDisplayField.text,
                component: customDisplayField.component,
                props: customDisplayField.props
            });
        }

        if (isCustomValueField(customDisplayField)) {
            result.push({
                text: customDisplayField.text,
                value: customDisplayField.value
            });
        }
    });

    return result;
}

function collectLowestFieldDefinitions(fieldTypeDefinitions: FieldTypeDefinition[]): FieldTypeDefinition[] {
    const result: FieldTypeDefinition[] = [];

    const traverse = (defs: FieldTypeDefinition[]) => {
        for (const def of defs) {
            const children = def.fieldTypes ?? [];
            if (children.length > 0) {
                traverse(children);
            } else {
                // only push if not dropped
                if (!droppedFieldIds.includes(def.id)) {
                    result.push(def);
                }
            }
        }
    };

    traverse(fieldTypeDefinitions);

    return result;
}

function collectFieldTypeIdNamePath(
    fieldTypeDefinitions: FieldTypeDefinition[],
    fieldTypeId: FieldTypeId
): string[] | null {
    const traverse = (defs: FieldTypeDefinition[], path: string[]): string[] | null => {
        for (const def of defs) {
            const newPath = [...path, def.text];

            if (def.id === fieldTypeId) {
                return newPath;
            }

            const children = def.fieldTypes ?? [];
            if (children.length > 0) {
                const result = traverse(children, newPath);
                if (result) return result;
            }
        }
        return null;
    };

    return traverse(fieldTypeDefinitions, []);
}

function getFormattedFieldTypeNameFromDefinitions(
    fieldTypeDefinitions: FieldTypeDefinition[],
    fieldTypeId: FieldTypeId
): string {
    const namesPath = collectFieldTypeIdNamePath(fieldTypeDefinitions, fieldTypeId);
    if (!exists(namesPath)) {
        return fieldTypeId;
    }

    // Join only first and last if a path has 2+ parts
    return namesPath.length > 1 ? `${namesPath[0]} - ${namesPath[namesPath.length - 1]}` : namesPath[0];
}